import React from "react";
import tpIcon from "../../assets/images/tp-icon.png";
import Avatar from "../common/Avatar";
import ClubCrest from "../common/ClubCrest";
import {
  formatRating,
  ratingTier,
  groupPlayers,
  standoutOf,
  playerInitials,
} from "./totwUtils";

// =====================================================================
// TOTW — the line-up list (frames 1a right column · 1f below the pitch).
// "Standout of the round" highlight card on top, then the same 1–11
// players grouped Goalkeeper / Defence / Midfield / Attack (brief data
// rule 5: grouping comes from the LINEUP position). The counter shows the
// REAL number of players (10-player lineups exist upstream).
//
// Row navigation (Marin finding 5, 2026-08-14): a row's chevron promises
// "to the profile", so the row (and the standout card) navigates straight
// to /search/player/<wyscout_id> — same target as the popover's View
// profile. Players WITHOUT a Wyscout match have no profile (brief data
// rule 4), so their rows render NO chevron and are not interactive at
// all: no dead affordance. (The previous wiring selected the pitch
// popover, but the click event bubbled into the page's close-all handler
// and reset the selection in the same event — a click did nothing.)
// =====================================================================

const ListSkeleton = ({ mobile }) => (
  <div className="flex flex-col" style={{ gap: mobile ? 10 : 9 }}>
    {Array.from({ length: 11 }).map((_, index) => (
      <div key={index} className="flex items-center animate-pulse" style={{ gap: mobile ? 10 : 11, padding: mobile ? "6px 0" : "7px 4px" }}>
        <div className="rounded-full bg-[#EEF2F7]" style={{ width: mobile ? 34 : 36, height: mobile ? 34 : 36 }} />
        <div className="flex flex-col flex-1" style={{ gap: 6 }}>
          <div className="rounded-[5px] bg-[#EEF2F7]" style={{ width: "52%", height: 10 }} />
          <div className="rounded-[5px] bg-[#F3F6F9]" style={{ width: "34%", height: 9 }} />
        </div>
        <div className="rounded-[5px] bg-[#EEF2F7]" style={{ width: mobile ? 30 : 34, height: mobile ? 13 : 14 }} />
      </div>
    ))}
  </div>
);

// Interaction props for a list surface that navigates to the player
// profile: only players WITH a Wyscout match get them (no dead affordance
// on unmatched rows — they have no profile to go to, brief data rule 4).
const profileNavProps = (player, onViewProfile) =>
  player.wyscout_id != null
    ? {
        role: "button",
        tabIndex: 0,
        onClick: (e) => {
          e.stopPropagation();
          onViewProfile(player);
        },
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onViewProfile(player);
          }
        },
      }
    : {};

const StandoutCard = ({ player, mobile, onViewProfile }) => (
  <div
    {...profileNavProps(player, onViewProfile)}
    className={`flex items-center bg-[#2283FF] rounded-xl ${player.wyscout_id != null ? "hover:bg-[#0B6BE0] cursor-pointer" : ""}`}
    style={{ gap: mobile ? 10 : 11, padding: mobile ? "11px 12px" : "12px 14px", minHeight: mobile ? 56 : undefined }}
  >
    <div className="relative flex-none">
      <div
        className="rounded-full flex items-center justify-center font-extrabold text-white overflow-hidden"
        style={{ width: mobile ? 36 : 38, height: mobile ? 36 : 38, background: "rgba(255,255,255,0.2)", border: "1.5px solid rgba(255,255,255,0.55)", fontSize: mobile ? 12 : 12.5 }}
      >
        {player.photo ? (
          <img src={player.photo} alt="" className="w-full h-full object-cover" />
        ) : (
          playerInitials(player)
        )}
      </div>
      <div
        className="absolute bg-white flex items-center justify-center"
        style={{ right: -5, bottom: -4, width: mobile ? 17 : 19, height: mobile ? 17 : 19, borderRadius: mobile ? 5 : 6 }}
      >
        <ClubCrest name={player.team?.name || ""} logoUrl={player.team?.logo || null} variant="shield" width={mobile ? 11 : 13} height={mobile ? 13 : 15} />
      </div>
    </div>
    <div className="flex flex-col min-w-0 flex-1" style={{ gap: mobile ? 1 : 2 }}>
      <div className="font-extrabold uppercase" style={{ fontSize: mobile ? 9 : 9.5, letterSpacing: "0.12em", color: "rgba(255,255,255,0.78)" }}>
        Standout of the round
      </div>
      <div data-no-translate="true" className="font-extrabold text-white whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: mobile ? 13.5 : 14 }}>
        {player.name || player.short_name || ""}
      </div>
      <div data-no-translate="true" className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: mobile ? 11 : 11.5, color: "rgba(255,255,255,0.8)" }}>
        {player.team?.name || ""}
      </div>
    </div>
    <div
      data-no-translate="true"
      className="flex-none bg-white font-extrabold tabular-nums"
      style={{ color: ratingTier(player.rating).solid, fontSize: mobile ? 13.5 : 14, borderRadius: 8, padding: mobile ? "3px 8px" : "4px 9px" }}
    >
      {formatRating(player.rating)}
    </div>
  </div>
);

const PlayerRow = ({ player, mobile, isStandout, onViewProfile }) => (
  <div
    {...profileNavProps(player, onViewProfile)}
    className={`flex items-center border-b border-[#F4F7FA] ${player.wyscout_id != null ? `cursor-pointer ${mobile ? "" : "rounded-[10px] hover:bg-[#F8FAFC]"}` : ""}`}
    style={{ gap: mobile ? 10 : 11, padding: mobile ? "6px 4px" : "8px 8px", minHeight: mobile ? 52 : undefined }}
  >
    <div className="relative flex-none">
      <Avatar
        src={player.photo}
        alt={player.name || ""}
        size={mobile ? 34 : 36}
        fallbackText={player.name || player.short_name || "?"}
      />
      {player.on_platform && (
        <img
          src={tpIcon}
          alt="On TransferPitch"
          className="absolute block bg-white"
          style={{ top: -5, right: -6, width: mobile ? 18 : 19, height: mobile ? 18 : 19, borderRadius: 6, boxShadow: "0 0 0 1.5px #FFFFFF" }}
        />
      )}
    </div>
    <div className="flex flex-col min-w-0 flex-1" style={{ gap: 2 }}>
      <div data-no-translate="true" className="font-bold text-[#0F172A] whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: mobile ? 13 : 13.5 }}>
        {player.name || player.short_name || playerInitials(player)}
      </div>
      <div className="flex items-center text-[#64748B] font-semibold" style={{ gap: mobile ? 5 : 6, fontSize: mobile ? 11 : 11.5 }}>
        <ClubCrest name={player.team?.name || ""} logoUrl={player.team?.logo || null} variant="shield" width={mobile ? 13 : 14} height={mobile ? 15 : 16} />
        <span data-no-translate="true" className="whitespace-nowrap overflow-hidden text-ellipsis">
          {player.team?.name || ""}
        </span>
      </div>
    </div>
    {isStandout && !mobile && (
      <span className="flex-none bg-[#2283FF] text-white font-extrabold uppercase rounded-full" style={{ fontSize: 10, letterSpacing: "0.06em", padding: "3px 8px" }}>
        Standout
      </span>
    )}
    {player.on_platform && !mobile && (
      <span className="flex-none bg-[#EAF3FF] text-[#1257B0] font-extrabold rounded-full" style={{ fontSize: 10.5, padding: "3px 8px" }}>
        On TP
      </span>
    )}
    <span
      data-no-translate="true"
      className="flex-none font-extrabold tabular-nums text-right"
      style={{
        minWidth: mobile ? undefined : 44,
        background: ratingTier(player.rating).bg,
        color: ratingTier(player.rating).fg,
        fontSize: mobile ? 12.5 : 13,
        borderRadius: 6,
        padding: "2px 7px",
      }}
    >
      {formatRating(player.rating)}
    </span>
    {player.wyscout_id != null && (
      <span aria-hidden="true" className="flex-none text-[#94A3B8] font-extrabold" style={{ fontSize: 15 }}>
        ›
      </span>
    )}
  </div>
);

const TOTWList = ({ players = [], loading = false, mobile = false, onViewProfile }) => {
  const standout = standoutOf(players);
  const groups = groupPlayers(players);
  return (
    <div
      className="bg-white border border-[#E9EEF4] shadow-[0_1px_2px_rgba(15,23,42,0.05)]"
      style={{ borderRadius: mobile ? 14 : 16, padding: mobile ? 14 : 18 }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: mobile ? 10 : 12 }}>
        <div className="font-extrabold text-[#0F172A]" style={{ fontSize: mobile ? 14.5 : 15 }}>
          Line-up
        </div>
        <div className="font-semibold text-[#94A3B8]" style={{ fontSize: mobile ? 11.5 : 12 }}>
          {loading ? "" : `${players.length} players`}
        </div>
      </div>
      {loading ? (
        <ListSkeleton mobile={mobile} />
      ) : (
        <div className="flex flex-col" style={{ gap: mobile ? 13 : 14 }}>
          {standout && <StandoutCard player={standout} mobile={mobile} onViewProfile={onViewProfile} />}
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col" style={{ gap: 2 }}>
              <div
                className="flex items-center bg-[#EAF3FF] text-[#1257B0] font-extrabold uppercase rounded-lg"
                style={{ gap: mobile ? 7 : 8, padding: mobile ? "7px 10px" : "7px 11px", fontSize: mobile ? 10 : 10.5, letterSpacing: "0.09em", marginBottom: mobile ? 3 : 4 }}
              >
                <span className="rounded-full bg-[#2283FF]" style={{ width: 5, height: 5 }} />
                <span>{group.label}</span>
              </div>
              {group.players.map((player) => (
                <PlayerRow
                  key={player.ts_player_id}
                  player={player}
                  mobile={mobile}
                  isStandout={standout && player.ts_player_id === standout.ts_player_id}
                  onViewProfile={onViewProfile}
                />
              ))}
            </div>
          ))}
          <div className="font-semibold text-[#94A3B8]" style={{ fontSize: mobile ? 10.5 : 11, paddingTop: mobile ? 0 : 2 }}>
            Ratings supplied by our data partner
          </div>
        </div>
      )}
    </div>
  );
};

export default TOTWList;
